import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";

type Dispute = {
	id: string;
	dealId: string;
	kind: "reconciliation_gap" | "fraud" | "payout_missed" | "contract_breach";
	openedByOrgId: string | null;
	openedByUserId: string | null;
	state: "open" | "evidence" | "review" | "resolved" | "cancelled";
	resolution: string | null;
	resolutionNotes: string | null;
	createdAt: number;
	updatedAt: number;
};

type DetailResp = {
	dispute: Dispute;
	deal: { id: string; brandOrgId: string; affiliateOrgId: string; state: string };
};

export function DisputeDetail() {
	const { id = "" } = useParams();
	const qc = useQueryClient();
	const { data, isLoading } = useQuery<DetailResp>({
		queryKey: ["dispute", id],
		queryFn: () => api.get(`/disputes/${id}`),
		enabled: !!id,
	});
	const [evidence, setEvidence] = useState("");
	const [resolution, setResolution] = useState<"upheld" | "dismissed" | "partial">("partial");
	const [notes, setNotes] = useState("");

	const evidenceMut = useMutation({
		mutationFn: () => api.post(`/disputes/${id}/evidence`, { text: evidence }),
		onSuccess: () => {
			setEvidence("");
			qc.invalidateQueries({ queryKey: ["dispute", id] });
			qc.invalidateQueries({ queryKey: ["disputes"] });
		},
	});
	const resolveMut = useMutation({
		mutationFn: () => api.post(`/disputes/${id}/resolve`, { resolution, notes }),
		onSuccess: () => {
			setNotes("");
			qc.invalidateQueries({ queryKey: ["dispute", id] });
			qc.invalidateQueries({ queryKey: ["disputes"] });
		},
	});

	if (isLoading || !data) return <div className="p-6">Loading…</div>;
	const { dispute, deal } = data;
	const terminal = dispute.state === "resolved" || dispute.state === "cancelled";

	return (
		<div className="space-y-4 p-6">
			<div>
				<Link to="/disputes" className="text-sm text-blue-700 hover:underline">
					← Disputes
				</Link>
			</div>
			<div>
				<div className="flex items-baseline justify-between">
					<h1 className="text-xl font-semibold">{dispute.kind}</h1>
					<div className="text-xs">
						{dispute.state}
						{dispute.resolution ? ` (${dispute.resolution})` : ""}
					</div>
				</div>
				<div className="font-mono text-xs text-neutral-500">{dispute.id}</div>
			</div>
			<div className="rounded border border-neutral-200 p-3 text-sm">
				<div className="text-xs text-neutral-500">Deal</div>
				<Link to={`/deals/${deal.id}`} className="font-mono text-blue-700 hover:underline">
					{deal.id}
				</Link>
				<div className="mt-1 text-xs text-neutral-500">
					brand <span className="font-mono">{deal.brandOrgId}</span> · affiliate{" "}
					<span className="font-mono">{deal.affiliateOrgId}</span>
				</div>
			</div>
			<div>
				<div className="text-xs text-neutral-500">Notes / log</div>
				<pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-neutral-50 p-3 text-xs">
					{dispute.resolutionNotes ?? "(no notes)"}
				</pre>
			</div>

			{!terminal && (
				<section className="space-y-2">
					<div className="text-sm font-medium">Add evidence</div>
					<textarea
						rows={3}
						value={evidence}
						onChange={(e) => setEvidence(e.target.value)}
						className="w-full rounded border px-2 py-1 text-sm"
					/>
					<button
						type="button"
						disabled={!evidence.trim() || evidenceMut.isPending}
						onClick={() => evidenceMut.mutate()}
						className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
					>
						Append evidence
					</button>
				</section>
			)}

			{!terminal && (
				<section className="space-y-2 rounded border border-amber-200 bg-amber-50 p-3">
					<div className="text-sm font-medium text-amber-900">Arbitrator resolve</div>
					<select
						value={resolution}
						onChange={(e) => setResolution(e.target.value as "upheld" | "dismissed" | "partial")}
						className="block rounded border px-2 py-1 text-sm"
					>
						<option value="partial">partial — split outcome</option>
						<option value="upheld">upheld — complaint valid</option>
						<option value="dismissed">dismissed — complaint invalid</option>
					</select>
					<textarea
						rows={3}
						placeholder="Resolution rationale"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						className="w-full rounded border px-2 py-1 text-sm"
					/>
					<button
						type="button"
						disabled={!notes.trim() || resolveMut.isPending}
						onClick={() => resolveMut.mutate()}
						className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50"
					>
						Resolve dispute
					</button>
				</section>
			)}
		</div>
	);
}
